import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { GuestUpgradeDialog } from './GuestUpgradeCard';
import { Sparkles } from 'lucide-react';

/**
 * GuestFirstDraftPrompt — 2026-08-27 (owner request; prod funnel showed only
 * 5% of guests ever finalize). One-time "save your team" prompts for guests:
 *   • after their FIRST successful draft (`fws:draft-success`)
 *   • 2026-08-28 — after they FINALIZE (`fws:finalize-success`), the peak
 *     "I'm invested" moment.
 * Each variant fires once-ever per user via its own localStorage key.
 */
const COPY = {
  draft: {
    title: "Nice pick! Don't lose your team",
    desc: "You're playing as a guest, so your team lives only on this device. Create a free account (takes 30 seconds) to keep your team, budget and rank forever — and sign in anywhere.",
    key: 'fws_guest_saveprompt_',
  },
  finalize: {
    title: "You're competing now!",
    desc: 'Your team is live on the world rankings. Create a free account so your team and rank are never lost — even if you switch devices.',
    key: 'fws_guest_savefinal_',
  },
};

export default function GuestFirstDraftPrompt() {
  const { user } = useAuth();
  const [variant, setVariant] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (!user?.is_guest) return undefined;
    const fire = (kind) => () => {
      const key = COPY[kind].key + user.id;
      try {
        if (localStorage.getItem(key)) return;
        localStorage.setItem(key, '1');
      } catch (e) { /* private mode — still show once this session */ }
      setVariant(kind);
    };
    const onDraft = (e) => {
      const d = e?.detail || {};
      // 2026-08-28 (iter186) — a roster-COMPLETING draft belongs to the
      // FinalizeNowPrompt; the guest save prompt fires later at finalize
      // (prevents two stacked dialogs blocking each other).
      if (d.roster_move === false && d.roster_capacity > 0 && d.roster_filled >= d.roster_capacity) return;
      fire('draft')();
    };
    const onFinalize = fire('finalize');
    window.addEventListener('fws:draft-success', onDraft);
    window.addEventListener('fws:finalize-success', onFinalize);
    return () => {
      window.removeEventListener('fws:draft-success', onDraft);
      window.removeEventListener('fws:finalize-success', onFinalize);
    };
  }, [user?.is_guest, user?.id]);

  if (!user?.is_guest) return null;
  const copy = COPY[variant] || COPY.draft;

  return (
    <>
      <Dialog open={!!variant} onOpenChange={(v) => { if (!v) setVariant(null); }}>
        <DialogContent className="sm:max-w-sm" data-testid="guest-first-draft-prompt">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[hsl(var(--accent))]" /> {copy.title}
            </DialogTitle>
            <DialogDescription>{copy.desc}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Button
              onClick={() => { setVariant(null); setShowForm(true); }}
              data-testid="guest-first-draft-save-btn"
            >
              Save my team
            </Button>
            <Button
              variant="ghost"
              onClick={() => setVariant(null)}
              data-testid="guest-first-draft-later-btn"
            >
              Maybe later
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <GuestUpgradeDialog open={showForm} onOpenChange={setShowForm} />
    </>
  );
}
